import { useQuery } from '@tanstack/react-query';
import { fetchStripeSubscription } from '../../services/subscriptions';
import useGetSubscription from './useGetSubscription';
import useUser from '../global/useUser';
import { LoadedUser } from '../../interfaces';

interface GetSubscriptionFromStripeParams {
  enabled?: boolean;
}

function useGetSubscriptionFromStripe({
  enabled: isEnabled = true,
}: GetSubscriptionFromStripeParams = {}) {
  const { user } = useUser<LoadedUser>();
  const { data: { subscription, isActive } = {} } = useGetSubscription();

  const businessId = user?.business?.id;
  const enabled = !!subscription?.id && isEnabled && !!businessId;

  const query = useQuery({
    queryKey: ['stripe-subscription', subscription?.id, businessId],
    queryFn: async () => {
      if (!subscription?.id || !businessId) return null;
      const data = await fetchStripeSubscription(subscription?.id, businessId);
      return data;
    },
    enabled,
  });

  return {
    data: query.data,
    state: {
      isActive,
      isCanceled:
        query.data?.status === 'canceled' || !!subscription?.isCanceled,
      isTrialing: query.data?.status === 'trialing',
      isPastDue: query.data?.status === 'past_due' && !isActive,
      isUnpaid: query.data?.status === 'unpaid',
      isPaymentFailed:
        ['unpaid', 'incomplete'].includes(query.data?.status!) ||
        (query.data?.status === 'past_due' && !isActive),
      isIncomplete: query.data?.status === 'incomplete',
      isIncompleteExpired: query.data?.status === 'incomplete_expired',
      isFree:
        query.data?.product?.price === 0 ||
        (query.data?.coupon?.valid &&
          ((query.data?.coupon.amount_off &&
            query.data?.coupon.amount_off >=
              (query.data?.product?.price ?? 0)) ||
            (query.data?.coupon.percent_off ?? 0) >= 100)),
    },
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    refetch: query.refetch,
  };
}

export default useGetSubscriptionFromStripe;
